// Documents landing page — tabbed list of Documents + Templates.
// Individual template design and document signing happen on dedicated
// routes (`/admin/documents/templates/:id` and `/admin/documents/:id`).
import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api from "../api";
import { toast } from "sonner";
import {
  FileText, UploadSimple, Plus, PencilSimple, ArrowRight,
  CheckCircle, XCircle, CircleNotch, Clock, WarningCircle,
} from "@phosphor-icons/react";

const TABS = [
  { key: "documents", label: "Documents" },
  { key: "templates", label: "Templates" },
];

const STATUS_META = {
  draft: { label: "Draft", color: "#5C738A", bg: "#e9eef3", icon: PencilSimple },
  ready: { label: "Ready", color: "#0d6b3f", bg: "#daf1e5", icon: Clock },
  signed: { label: "Signed", color: "#012340", bg: "#d0e0ee", icon: CheckCircle },
  cancelled: { label: "Cancelled", color: "#8a3535", bg: "#f4dcdc", icon: XCircle },
};

export default function DocumentsCenter() {
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "documents";

  return (
    <div className="flex flex-col h-full min-h-0" data-testid="documents-center">
      <div className="mb-5 flex-shrink-0">
        <div className="text-[11px] font-bold uppercase tracking-[0.3em] text-[#5C738A]">Field Operations</div>
        <h1 className="font-heading text-3xl font-black text-[#012340] mt-1">Documents</h1>
        <p className="text-sm text-[#5C738A] mt-1">iPad-friendly signing for waste service proposals & agreements.</p>
      </div>

      <div className="border-b border-[#012340]/10 flex gap-1 mb-4 flex-shrink-0">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setParams({ tab: t.key })}
            data-testid={`docs-tab-${t.key}`}
            className={`px-5 py-2 -mb-px text-[12px] font-bold uppercase tracking-[0.22em] border-b-2 ${
              tab === t.key
                ? "border-[#CFAB7A] text-[#012340]"
                : "border-transparent text-[#5C738A] hover:text-[#012340]"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto pr-1">
        {tab === "documents" ? <DocumentsPanel /> : <TemplatesPanel />}
      </div>
    </div>
  );
}

function DocumentsPanel() {
  const [rows, setRows] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [creating, setCreating] = useState(false);
  const nav = useNavigate();
  const reload = async () => {
    try {
      const [docsRes, tplRes] = await Promise.all([
        api.get("/documents"),
        api.get("/document-templates"),
      ]);
      setRows(docsRes.data);
      setTemplates(tplRes.data.filter((t) => t.active !== false));
    } catch (e) {
      toast.error("Could not load documents");
      setRows([]);
    }
  };
  useEffect(() => { reload(); }, []);

  const createNew = async (templateId) => {
    setCreating(true);
    try {
      const { data } = await api.post("/documents", { template_id: templateId });
      nav(`/admin/documents/${data.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create document");
    } finally { setCreating(false); }
  };

  if (rows === null) return <div className="p-6 text-[#5C738A]"><CircleNotch className="animate-spin inline mr-2" /> Loading…</div>;

  return (
    <div>
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="text-sm text-[#5C738A]">
          {rows.length} document{rows.length === 1 ? "" : "s"}
        </div>
        {templates.length > 0 && (
          <div className="flex items-center gap-2 flex-wrap">
            {templates.map((t) => (
              <button
                key={t.id}
                onClick={() => createNew(t.id)}
                disabled={creating}
                data-testid={`new-doc-from-${t.id}`}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-sm bg-[#012340] text-white text-xs font-bold uppercase tracking-[0.18em] hover:bg-[#012340]/90 disabled:opacity-50"
              >
                <Plus size={14} weight="bold" /> New from “{t.name}”
              </button>
            ))}
          </div>
        )}
      </div>

      {rows.length === 0 ? (
        <div className="p-10 border border-dashed border-[#012340]/15 rounded-sm text-center text-[#5C738A]" data-testid="documents-empty">
          <FileText size={32} weight="bold" className="mx-auto mb-2 text-[#012340]/40" />
          No documents yet. Upload a template on the Templates tab, then create a document from it.
        </div>
      ) : (
        <table className="w-full text-sm" data-testid="documents-table">
          <thead>
            <tr className="text-[10px] uppercase tracking-[0.22em] text-[#5C738A] font-bold text-left">
              <th className="py-2 pr-3">Document</th>
              <th className="py-2 pr-3">Template</th>
              <th className="py-2 pr-3">Customer</th>
              <th className="py-2 pr-3">Status</th>
              <th className="py-2 pr-3">Updated</th>
              <th className="py-2"></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const s = STATUS_META[r.status] || STATUS_META.draft;
              return (
                <tr key={r.id} className="border-t border-[#012340]/8" data-testid={`document-row-${r.id}`}>
                  <td className="py-3 pr-3 text-[#012340] font-semibold">{r.name}</td>
                  <td className="py-3 pr-3 text-[#5C738A]">{r.template_name}</td>
                  <td className="py-3 pr-3 text-[#5C738A]">{r.customer_snapshot?.business_name || "—"}</td>
                  <td className="py-3 pr-3">
                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded-sm text-[10px] font-bold uppercase tracking-[0.18em]" style={{ background: s.bg, color: s.color }}>
                      <s.icon size={12} weight="bold" /> {s.label}
                    </span>
                  </td>
                  <td className="py-3 pr-3 text-[#5C738A]">{r.updated_at?.slice(0, 16).replace("T", " ")}</td>
                  <td className="py-3 text-right">
                    <Link to={`/admin/documents/${r.id}`} data-testid={`open-doc-${r.id}`} className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-[0.18em] text-[#012340] hover:text-[#CFAB7A]">
                      Open <ArrowRight size={12} weight="bold" />
                    </Link>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}

function TemplatesPanel() {
  const [rows, setRows] = useState(null);
  const [uploading, setUploading] = useState(false);
  const nav = useNavigate();
  const reload = async () => {
    try { const { data } = await api.get("/document-templates"); setRows(data); }
    catch { toast.error("Could not load templates"); setRows([]); }
  };
  useEffect(() => { reload(); }, []);

  const onPick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    // Lenient PDF detection — some browsers (esp. iPad Safari) drop the
    // MIME type for .PDF (uppercase) files or report application/x-pdf.
    const looksLikePdf =
      file.type === "application/pdf" ||
      file.type === "application/x-pdf" ||
      /\.pdf$/i.test(file.name);
    if (!looksLikePdf) {
      toast.error("Please choose a PDF file");
      return;
    }
    // Default the template name to the filename minus extension —
    // avoids window.prompt which is unreliable on iPad Safari. Admins
    // can rename inside the designer.
    const name = file.name.replace(/\.pdf$/i, "").replace(/[_-]+/g, " ").trim() || "Untitled template";
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("name", name);
      const { data } = await api.post("/document-templates", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Uploaded "${data.name}" — map fields next`);
      nav(`/admin/documents/templates/${data.id}`);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : (err?.message || "Upload failed");
      toast.error(`Upload failed: ${msg}`);
      // Surface the full error so we can debug from console if this ever fires again.
      // eslint-disable-next-line no-console
      console.error("template upload error:", err);
    } finally { setUploading(false); }
  };

  if (rows === null) return <div className="p-6 text-[#5C738A]"><CircleNotch className="animate-spin inline mr-2" /> Loading…</div>;

  return (
    <div>
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="text-sm text-[#5C738A]">{rows.length} template{rows.length === 1 ? "" : "s"}</div>
        <label className="inline-flex items-center gap-2 px-4 py-2 rounded-sm bg-[#CFAB7A] text-[#012340] text-xs font-bold uppercase tracking-[0.18em] hover:brightness-105 cursor-pointer" data-testid="upload-template">
          <UploadSimple size={14} weight="bold" /> {uploading ? "Uploading…" : "Upload Template PDF"}
          <input type="file" accept="application/pdf" onChange={onPick} disabled={uploading} style={{ display: "none" }} />
        </label>
      </div>

      {rows.length === 0 ? (
        <div className="p-10 border border-dashed border-[#012340]/15 rounded-sm text-center text-[#5C738A]" data-testid="templates-empty">
          <FileText size={32} weight="bold" className="mx-auto mb-2 text-[#012340]/40" />
          No templates yet. Upload the ZMEX Commercial Waste Service Proposal & Agreement PDF above.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {rows.map((t) => (
            <Link
              key={t.id}
              to={`/admin/documents/templates/${t.id}`}
              data-testid={`template-card-${t.id}`}
              className={`block p-5 border rounded-sm bg-white ${t.active === false ? "opacity-60" : ""} border-[#012340]/10 hover:border-[#CFAB7A] transition-colors`}
            >
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="font-heading text-lg font-black text-[#012340]">{t.name}</div>
                  <div className="text-xs text-[#5C738A] mt-1">{t.page_count} pages · {(t.fields || []).length} fields mapped</div>
                </div>
                {t.active === false && <WarningCircle size={16} weight="bold" className="text-[#b23]" />}
              </div>
              {t.description && <p className="text-sm text-[#5C738A] mt-2">{t.description}</p>}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
